import { EventSourcePolyfill } from 'event-source-polyfill';
import dayjs                   from 'dayjs';

const MIN_RENEWAL_DELAY_SECONDS = 30;

export type MercureEvent = { data: string, type: string };
export type OnMessageFunction = (data: MessageTemplate, e: MercureEvent) => boolean | void | Promise<boolean | void>;
export type MessageTemplate = { kind: string, objectUrn: Urn | null, data: any };

/** Contrat minimal qu'un modèle doit remplir pour servir de token d'authentification Mercure. */
export type MercureTokenModel = {
	jwt: string;
	expMoment: Dayjs;
	clear(): MercureTokenModel;
	set(attributes: object): MercureTokenModel;
	fetch(): Promise<unknown>;
};

export type MercureListenerOptions<T extends MercureTokenModel, A extends object> = {
	token: T;
	/** Attributs passés à `token.set()` avant le fetch. */
	tokenAttributes: A | (() => A);
	onMessage: OnMessageFunction;
};

export default class MercureListener<T extends MercureTokenModel, A extends object = { id?: id }> {
	private _authTimeout?: ReturnType<typeof setTimeout>;
	private _eventSource?: EventSourcePolyfill;

	private _executeMessageFunction?: OnMessageFunction;

	private _token: T;
	private _tokenAttributes: () => A;

	public constructor(options: MercureListenerOptions<T, A>) {
		this._token = options.token;

		const attributes = options.tokenAttributes;
		this._tokenAttributes = typeof attributes === 'function'
			? attributes as () => A
			: () => attributes;

		this._executeMessageFunction = options.onMessage;
	}

	public setOnMessage(onMessage?: OnMessageFunction) {
		this._executeMessageFunction = onMessage;
	}

	public async start(topics: string[] = []): Promise<void> {
		this.stop();

		await this._fetchToken();

		const mercureService = window.APP_CONFIG.services.find(service => service.id === 'mercure');
		const url = new URL(mercureService?.configurations.api_endpoint || '');

		topics.forEach(topic => url.searchParams.append('topic', topic));

		this._eventSource = new EventSourcePolyfill(url.toString().replace('/?', '?'), {
			headers: { 'Authorization': `Bearer ${this._token.jwt}` },
		});

		this._eventSource.onmessage = this._onMessage;
	}

	public stop(): void {
		this._eventSource?.close();

		if (this._authTimeout) {
			clearTimeout(this._authTimeout);
		}
	}

	private async _fetchToken() {
		await this._token
			.clear()
			.set(this._tokenAttributes())
			.fetch();

		const timeRemaining = this._token.expMoment.diff(dayjs(), 'second');

		this._authTimeout = setTimeout(this._fetchToken.bind(this), Math.max(timeRemaining, MIN_RENEWAL_DELAY_SECONDS) * 1000);
	}

	private _onMessage = (e: MercureEvent): void => {
		this._executeMessageFunction?.(JSON.parse(e.data) as MessageTemplate, e);
	};
}
